import styled from 'styled-components';

export const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing(3)};
  padding-bottom: ${({ theme }) => theme.spacing(4)};
`;

export const Titles = styled.div`
  flex: 1;
  min-width: 0;
`;

export const Eyebrow = styled.div`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-bottom: ${({ theme }) => theme.spacing(1)};
`;

export const Title = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.01em;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;

  &:focus {
    outline: none;
  }
`;

export const CopyBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  padding: ${({ theme }) => `${theme.spacing(0.5)} ${theme.spacing(2)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  font: inherit;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

export const Name = styled.p`
  margin: ${({ theme }) => `${theme.spacing(2)} 0 0`};
  font-size: 15px;
  line-height: 1.5;
`;

export const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(4)}`};
  margin-top: ${({ theme }) => theme.spacing(2)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};

  & strong {
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: 600;
  }
`;

export const Section = styled.section`
  margin-top: ${({ theme }) => theme.spacing(6)};
`;

export const SectionTitle = styled.h3`
  margin: 0 0 ${({ theme }) => theme.spacing(2)};
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.colors.text.muted};
`;

export const Path = styled.ol`
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: ${({ theme }) => theme.spacing(1)};
  font-size: 13px;

  & li {
    display: flex;
    gap: ${({ theme }) => theme.spacing(2)};
  }

  & .kind {
    flex: none;
    min-width: 84px;
    color: ${({ theme }) => theme.colors.text.muted};
  }
`;

export const ContentList = styled.ul`
  margin: 0;
  padding-left: ${({ theme }) => theme.spacing(5)};
  display: grid;
  gap: ${({ theme }) => theme.spacing(1)};
  font-size: 13px;
  line-height: 1.5;
`;

export const TableWrap = styled.div`
  overflow-x: auto;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
`;

export const Grid = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th,
  td {
    text-align: left;
    padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    vertical-align: top;
  }

  th {
    font-weight: 600;
    font-size: 12px;
    color: ${({ theme }) => theme.colors.text.secondary};
    background: ${({ theme }) => theme.colors.surface};
  }

  tr.group td {
    font-weight: 600;
    font-size: 12px;
    color: ${({ theme }) => theme.colors.text.secondary};
    background: ${({ theme }) => theme.colors.surface};
  }

  th.num,
  td.num {
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }

  td.code {
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }

  td.code.small {
    font-size: 12px;
  }

  @media (max-width: 520px) {
    td.code.small {
      white-space: normal;
      min-width: 120px;
    }
  }

  td.nowrap {
    white-space: nowrap;
  }

  tr.clickable {
    cursor: pointer;
  }

  tr.sub td {
    background: ${({ theme }) => theme.colors.surface};
    font-size: 12px;
  }

  tr.clickable:hover td {
    background: ${({ theme }) => theme.colors.surface};
  }

  tr.added td {
    color: ${({ theme }) => theme.colors.status.success};
  }

  tr.removed td {
    color: ${({ theme }) => theme.colors.status.danger};
    text-decoration: line-through;
  }

  tr.changed td.num.after {
    color: ${({ theme }) => theme.colors.status.warning};
    font-weight: 600;
  }
`;

export const Muted = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

export const MutedNote = styled(Muted)`
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

export const Err = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.status.danger};
`;

export const CoefNote = styled.div`
  margin-top: ${({ theme }) => theme.spacing(1)};
  font-size: 12px;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const GroupToggle = styled.button`
  margin-left: ${({ theme }) => theme.spacing(2)};
  padding: 0;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.primary};
  font: inherit;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

export const ValuesGrid = styled.dl`
  margin: 0;
  display: grid;
  grid-template-columns: minmax(180px, max-content) 1fr;
  gap: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(4)}`};
  font-size: 13px;

  & dt {
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  & dd {
    margin: 0;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing(0.5)};

    & dd {
      margin-bottom: ${({ theme }) => theme.spacing(1.5)};
    }
  }
`;

export const DiffRow = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: ${({ theme }) => theme.spacing(2)};
  font-size: 13px;
  padding: ${({ theme }) => `${theme.spacing(1.5)} 0`};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  & .label {
    color: ${({ theme }) => theme.colors.text.muted};
  }

  & del {
    color: ${({ theme }) => theme.colors.status.danger};
  }

  & ins {
    text-decoration: none;
    color: ${({ theme }) => theme.colors.status.success};
  }

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;
